import { ChevronRight } from 'lucide-react';
import { DevToolsCopyBox } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsCopyBox';
import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import { WebRTCConnectionStateBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/WebRTCTab/WebRTCConnectionStateBadge';
import type { WebRTCPeerConnectionCardProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/WebRTCTab/WebRTCPeerConnectionCard/index.types';
import {
  SURFACE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React, { useState } from 'react';
import { cn } from '~/lib/utils';

const SDP_PRE_CLASSES =
  'mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-[7px] border border-zinc-800 bg-zinc-950 px-2.5 py-2 font-sp-mono text-[10.5px] leading-[1.65] text-zinc-300';

const SUMMARY_CLASSES =
  'flex cursor-pointer items-center gap-1 text-[11px] font-medium text-emerald-400 hover:text-emerald-300';

/**
 * WebRTCPeerConnectionCard - One peer connection: its three state machines, the SDP summary, and the raw SDP behind a disclosure.
 */
export const WebRTCPeerConnectionCard: React.FC<
  WebRTCPeerConnectionCardProps
> = ({ info }) => {
  const [showLocalSdp, setShowLocalSdp] = useState(false);
  const [showRemoteSdp, setShowRemoteSdp] = useState(false);

  const states = [
    { label: 'Connection', state: info.connectionState },
    { label: 'ICE', state: info.iceConnectionState },
    { label: 'Signaling', state: info.signalingState },
  ];

  const sdps = [
    {
      label: 'Local SDP',
      type: info.localSdpType,
      candidates: info.localCandidatesCount,
    },
    {
      label: 'Remote SDP',
      type: info.remoteSdpType,
      candidates: info.remoteCandidatesCount,
    },
  ];

  return (
    <div className={SURFACE.card}>
      <div className="border-b border-zinc-800 p-2.5">
        <DevToolsCopyBox value={info.callId} label="Call ID" />
      </div>

      <div className="flex flex-col gap-2.5 p-3">
        <div className="grid grid-cols-3 gap-1.5">
          {states.map(({ label, state }) => (
            <div key={label} className={cn('px-2 py-[7px]', SURFACE.inset)}>
              <DevToolsMicroLabel className="mb-1">{label}</DevToolsMicroLabel>
              <WebRTCConnectionStateBadge state={state} />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          {sdps.map(({ label, type, candidates }) => (
            <div key={label} className={cn('px-2 py-[7px]', SURFACE.inset)}>
              <DevToolsMicroLabel>{label}</DevToolsMicroLabel>
              <div className="mt-0.5 flex items-baseline gap-1.5">
                <span className="font-sp-mono text-[12.5px] font-medium text-zinc-200">
                  {type || 'N/A'}
                </span>
                <span className="text-[10px] text-zinc-400">
                  {candidates} candidates
                </span>
              </div>
            </div>
          ))}
        </div>

        {info.localSdp && (
          <details
            open={showLocalSdp}
            onToggle={(e) =>
              setShowLocalSdp((e.target as HTMLDetailsElement).open)
            }
          >
            <summary className={SUMMARY_CLASSES}>
              <ChevronRight
                className={cn(
                  'h-3 w-3 transition-transform',
                  showLocalSdp && 'rotate-90'
                )}
              />
              Local SDP
            </summary>
            <pre className={SDP_PRE_CLASSES}>{info.localSdp}</pre>
          </details>
        )}

        {info.remoteSdp && (
          <details
            open={showRemoteSdp}
            onToggle={(e) =>
              setShowRemoteSdp((e.target as HTMLDetailsElement).open)
            }
          >
            <summary className={SUMMARY_CLASSES}>
              <ChevronRight
                className={cn(
                  'h-3 w-3 transition-transform',
                  showRemoteSdp && 'rotate-90'
                )}
              />
              Remote SDP
            </summary>
            <pre className={SDP_PRE_CLASSES}>{info.remoteSdp}</pre>
          </details>
        )}
      </div>
    </div>
  );
};
